import { useState } from 'react'
import classNames from 'classnames'
import { Field } from 'formik'
import _ from 'lodash'
import { ArrowRightIcon } from '@heroicons/react/outline'
import Input from 'components/atoms/Input'
import Selection from 'components/molecules/Selection'
import notify from 'components/atoms/Notify'
import { formatPrice } from 'lib/utils'
import { IoInformationCircleOutline } from 'react-icons/io5'
import { BOOKING_SECTION_TITLE, CREATE_BOOKING_BTN_PREV, CREATE_BOOKING_FIELD, CREATE_BOOKING_SELECTION, CREATE_BOOKING_TOTAL } from '../../bookingUiTokens'

const summaryRowClass = 'flex items-center justify-between gap-3 border-b border-slate-200/60 py-2.5 text-sm last:border-b-0'

const ReviewStep = ({
  t,
  values,
  errors,
  touched,
  setFieldValue,
  setFieldTouched,
  setTouched,
  validateForm,
  carDetail,
  selectedCar,
  deliveryData,
  isDeliveryPricingLoading,
  pricingError,
  formatDate,
  calculateRentalDays,
  transferReceiptFile,
  setTransferReceiptFile,
  isSubmitting,
  createBooking,
  onPrevious,
  isCompanyBookingFlow,
}) => {
  const [createAttempted, setCreateAttempted] = useState(false)
  const makeLabel = _.isString(carDetail?.car?.make) ? carDetail.car.make : carDetail?.car?.make?.make || ''
  const modelLabel = _.isString(carDetail?.car?.model) ? carDetail.car.model : carDetail?.car?.model?.modal || ''

  const isDeliver = values.deliveryMethod === 'deliver'
  const useDeliveryPricing = Boolean(deliveryData)
  const pricingPending = isDeliveryPricingLoading || pricingError
  const apiNumberOfDays = Number(useDeliveryPricing ? deliveryData?.numberOfDays : carDetail?.payment?.numberOfDays)
  const rentalDays = apiNumberOfDays > 0 ? apiNumberOfDays : calculateRentalDays(values.rentalStartDate, values.rentalEndDate) || 1

  const rentalPrice = useDeliveryPricing ? deliveryData?.priceWithDays || 0 : carDetail?.payment?.priceWithDays || 0

  const insuranceFee = useDeliveryPricing ? deliveryData?.insuranceFee || 0 : carDetail?.payment?.insuranceFee || 0

  const vatPct = useDeliveryPricing ? deliveryData?.vatTaxPercentage || 0 : carDetail?.payment?.vatTaxPercentage || 0

  const vat = useDeliveryPricing
    ? values.insurance
      ? deliveryData?.vatTaxWithInsurance || 0
      : deliveryData?.vatTaxWithoutInsurance || 0
    : values.insurance
    ? carDetail?.payment?.vatTaxWithInsurance || 0
    : carDetail?.payment?.vatTaxWithoutInsurance || 0

  const officeDeliveryCost =
    isDeliver && useDeliveryPricing ? deliveryData?.officeDeliveryCost ?? Math.max(0, (deliveryData?.deliveryCost || 0) - (deliveryData?.ajarleeDeliveryCost || 0)) : 0
  const ajarleeServiceFee = isDeliver && useDeliveryPricing ? deliveryData?.ajarleeDeliveryCost || 0 : 0

  // Extras are priced per day and VAT-able, same as the server's resolveExtras; it re-prices on create.
  const availableExtras = deliveryData?.availableExtras || []
  const selectedExtras = _.filter(availableExtras, extra => _.includes(values.extras, extra.key))
  const extrasAmount = _.round(
    _.sumBy(selectedExtras, extra => extra.pricePerDay * rentalDays),
    3
  )
  const extrasVat = _.round((extrasAmount * vatPct) / 100, 3)
  const toggleExtra = (key, checked) => setFieldValue('extras', checked ? [...(values.extras || []), key] : _.without(values.extras, key))

  const totalAmount = pricingPending ? 0 : rentalPrice + vat + extrasVat + extrasAmount + officeDeliveryCost + ajarleeServiceFee
  const formatSummaryAmount = amount => (pricingPending ? '—' : formatPrice(amount, true))

  const companyPaymentComplete =
    isCompanyBookingFlow &&
    values.paymentStatus &&
    values.paymentMethod &&
    (values.paymentMethod !== 'transfer' || (values.transferReferenceNumber && values.transferReceiptFileName))

  const handleSubmit = async () => {
    setCreateAttempted(true)
    if (isCompanyBookingFlow) {
      if (!values.paymentStatus) {
        setFieldTouched('paymentStatus', true, false)
        notify.error(t('bookingProfile.validation.paymentStatusRequired'))
        return
      }
      if (!values.paymentMethod) {
        setFieldTouched('paymentMethod', true, false)
        notify.error(t('bookingProfile.validation.paymentMethodRequired'))
        return
      }
    } else if (!values.paymentMethod) {
      setFieldTouched('paymentMethod', true, false)
      notify.error(t('bookingProfile.validation.paymentMethodRequired'))
      return
    }

    if (values.paymentMethod === 'direct_link') {
      setFieldTouched('status', true, false)
      setFieldTouched('paymentStatus', true, false)
    }

    const validationErrors = await validateForm()
    const errorKeys = _.keys(validationErrors || {})
    if (errorKeys.length > 0) {
      const nextTouched = { ...touched }
      _.forEach(errorKeys, key => {
        nextTouched[key] = true
      })
      if (values.paymentMethod === 'direct_link') {
        nextTouched.status = true
        nextTouched.paymentStatus = true
      }
      setTouched(nextTouched, false)
      if (validationErrors.status) {
        notify.error(validationErrors.status)
      } else if (validationErrors.paymentStatus) {
        notify.error(validationErrors.paymentStatus)
      } else if (validationErrors.rentalEndDate) {
        notify.error(validationErrors.rentalEndDate)
      } else if (values.paymentMethod === 'transfer' && (validationErrors.transferReferenceNumber || validationErrors.transferReceiptFileName)) {
        notify.error(t('bookingProfile.messages.transferFieldsRequired'))
      }
      return
    }
    createBooking(values)
  }

  const handlePaymentChange = value => {
    if (!value?.id) return

    const deselecting = values.paymentMethod === value.id
    setCreateAttempted(false)

    if (deselecting) {
      setFieldValue('paymentMethod', '', true)
      setFieldValue('transferReferenceNumber', '', false)
      setFieldValue('transferReceiptFileName', '', false)
      setTransferReceiptFile(null)
      if (!isCompanyBookingFlow) {
        setFieldValue('status', '', false)
        setFieldValue('paymentStatus', '', false)
      }
      if (value.id === 'checkout') {
        setFieldValue('sentPaymentEmail', false, false)
      }
    } else {
      setFieldValue('paymentMethod', value.id, true)
      if (!isCompanyBookingFlow) {
        setFieldValue('status', '', false)
        setFieldValue('paymentStatus', '', false)
      }
      if (value.id !== 'transfer') {
        setFieldValue('transferReferenceNumber', '', false)
        setFieldValue('transferReceiptFileName', '', false)
        setTransferReceiptFile(null)
      }
      if (value.id !== 'checkout') {
        setFieldValue('sentPaymentEmail', false, false)
      }
    }

    setTouched(
      {
        ...touched,
        paymentMethod: true,
        transferReferenceNumber: false,
        transferReceiptFileName: false,
        ...(!isCompanyBookingFlow ? { status: false, paymentStatus: false } : {}),
      },
      false
    )
  }

  const handleStatusChange = value => {
    if (!value?.id) return
    setFieldValue('status', value.id, true)
    setFieldTouched('status', true, false)
  }

  const handlePaymentStatusChange = value => {
    if (!value?.id) return
    setFieldValue('paymentStatus', value.id, true)
    setFieldTouched('paymentStatus', true, false)
    if (isCompanyBookingFlow && value.id === 'unpaid' && values.paymentMethod === 'transfer') {
      setFieldValue('paymentMethod', '', true)
      setFieldValue('transferReferenceNumber', '', false)
      setFieldValue('transferReceiptFileName', '', false)
      setTransferReceiptFile(null)
    }
  }

  const showFieldError = name => Boolean(errors[name] && (touched[name] || createAttempted))

  const paidUnpaidPaymentStatusOptions = [
    { id: 'paid', name: t('updateBooking.paymentStatusOptions.paid') },
    { id: 'unpaid', name: t('updateBooking.paymentStatusOptions.unpaid') },
  ]

  const adminBookingStatusOptions = [
    { id: 'confirmed', name: t('updateBooking.statusOptions.confirmed') },
    { id: 'pending', name: t('updateBooking.statusOptions.pending') },
    { id: 'userReview', name: t('updateBooking.statusOptions.userReview') },
    { id: 'inProgress', name: t('updateBooking.statusOptions.inProgress') },
    { id: 'completed', name: t('updateBooking.statusOptions.completed') },
  ]

  const companyPaymentMethodOptions = [
    { id: 'pos', name: t('bookingProfile.paymentMethod.pos') },
    ...(values.paymentStatus !== 'unpaid' ? [{ id: 'transfer', name: t('bookingProfile.paymentMethod.accountTransfer') }] : []),
  ]

  const renderTransferFields = () => (
    <>
      <div className="min-w-0">
        <label htmlFor="transferReferenceNumber" className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">
          {t('bookingProfile.labels.transferReferenceNumber')}
        </label>
        <Field
          type="text"
          name="transferReferenceNumber"
          as={Input}
          id="transferReferenceNumber"
          extraClassName="w-full"
          className={CREATE_BOOKING_FIELD}
          placeholder={t('bookingProfile.placeholders.transferReferenceNumber')}
        />
      </div>
      <div className="mt-5 md:col-span-2">
        <label htmlFor="transferReceipt" className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">
          {t('bookingProfile.labels.transferReceipt')}
        </label>
        <div className="rounded-xl border-2 border-dashed border-slate-300/90 bg-slate-50/80 px-4 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <label
              htmlFor="transferReceipt"
              className="inline-flex cursor-pointer items-center justify-center rounded-lg bg-dark-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-dark-700"
            >
              {t('bookingProfile.labels.chooseFile')}
            </label>
            <span className="text-sm text-slate-600">{transferReceiptFile?.name || t('bookingProfile.labels.noFileChosen')}</span>
            <input
              key={values.paymentMethod === 'transfer' ? 'transfer-receipt' : 'no-transfer'}
              id="transferReceipt"
              type="file"
              accept="image/*,.pdf,application/pdf"
              className="sr-only"
              onChange={e => {
                const file = e.target.files?.[0]
                setTransferReceiptFile(file || null)
                setFieldValue('transferReceiptFileName', file?.name || '', false)
                setTimeout(() => {
                  validateForm()
                }, 0)
              }}
            />
          </div>
          {errors.transferReceiptFileName && touched.transferReceiptFileName && <div className="mt-2 text-sm text-red-500">{errors.transferReceiptFileName}</div>}
          <p className="mt-2 text-xs text-slate-500">{t('bookingProfile.hints.transferReceiptTypes')}</p>
        </div>
      </div>
    </>
  )

  return (
    <div className="grid gap-6 pt-2 lg:grid-cols-[minmax(0,1fr)_min(22rem,100%)] lg:items-start lg:gap-8">
      {/* Left: rent info + bill */}
      <div className="min-w-0 space-y-6">
        <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm ring-1 ring-slate-900/[0.03] sm:p-6">
          <div className={BOOKING_SECTION_TITLE}>{t('bookingProfile.labels.rentInfo')}</div>

          <div className="mt-5 flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{carDetail?.car?.marketSegment || '—'}</p>
              <h3 className="mt-1 text-xl font-extrabold tracking-tight text-slate-900 sm:text-2xl">
                {makeLabel} {modelLabel}
              </h3>
              <p className="mt-2 text-2xl font-bold tabular-nums text-dark-600 sm:text-3xl">
                {formatPrice(carDetail?.dailyRate || 0, true)}
                <span className="ml-1.5 text-sm font-semibold text-slate-500 sm:text-base">
                  {t('common.currency')} {t('bookingProfile.labels.perDay')}
                </span>
              </p>
            </div>
            <img
              src={carDetail?.carImages?.[0]?.url || '/placeholder-car.jpg'}
              alt=""
              className="h-28 w-full max-w-[11rem] shrink-0 rounded-xl object-cover shadow-md ring-1 ring-slate-200/60 sm:h-32 sm:w-44"
            />
          </div>

          <div className="mt-5 rounded-xl bg-sky-50/90 px-4 py-3 ring-1 ring-sky-100/80 sm:px-5 sm:py-4">
            <div className={classNames(summaryRowClass, 'border-b border-sky-200/60')}>
              <span className="text-slate-600">{t('bookingProfile.labels.pickupDate')}</span>
              <span className="text-right font-semibold tabular-nums text-slate-900">{formatDate(values.rentalStartDate)}</span>
            </div>
            <div className={classNames(summaryRowClass, 'border-b border-sky-200/60')}>
              <span className="text-slate-600">{t('bookingProfile.labels.dropOffDate')}</span>
              <span className="text-right font-semibold tabular-nums text-slate-900">{formatDate(values.rentalEndDate)}</span>
            </div>
            <div className="flex items-center justify-between gap-3 py-2.5 text-sm">
              <span className="text-slate-600">{t('bookingProfile.labels.numberOfDays')}</span>
              <span className="font-bold tabular-nums text-dark-600">
                {rentalDays} {t('bookingProfile.labels.daysUnit')}
              </span>
            </div>
          </div>
        </div>

        {isCompanyBookingFlow && (
          <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm ring-1 ring-slate-900/[0.03] sm:p-6">
            <div className={BOOKING_SECTION_TITLE}>{t('bookingProfile.labels.bill')}</div>

            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
              <div className="min-w-0">
                <label htmlFor="paymentStatus" className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">
                  {t('bookingProfile.labels.paymentStatus')}
                </label>
                <Selection
                  onChange={handlePaymentStatusChange}
                  title={t('bookingProfile.labels.selectPaymentStatus')}
                  list={paidUnpaidPaymentStatusOptions}
                  value={values.paymentStatus || ''}
                  className={CREATE_BOOKING_SELECTION}
                  error={showFieldError('paymentStatus')}
                />
                {showFieldError('paymentStatus') && <div className="mt-2 text-sm text-red-600">{errors.paymentStatus}</div>}
              </div>

              <div className="min-w-0">
                <label htmlFor="payment" className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">
                  {t('bookingProfile.labels.paymentMethod')}
                </label>
                <Selection
                  onChange={handlePaymentChange}
                  title={t('bookingProfile.labels.selectPaymentMethod')}
                  list={companyPaymentMethodOptions}
                  value={values.paymentMethod || ''}
                  className={CREATE_BOOKING_SELECTION}
                />
                {errors.paymentMethod && touched.paymentMethod && <div className="mt-1 text-sm text-red-500">{errors.paymentMethod}</div>}
              </div>

              {values.paymentMethod === 'transfer' && renderTransferFields()}
            </div>
          </div>
        )}

        {!isCompanyBookingFlow && (
          <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm ring-1 ring-slate-900/[0.03] sm:p-6">
            <div className={BOOKING_SECTION_TITLE}>{t('bookingProfile.labels.bill')}</div>

            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
              <div className="min-w-0">
                <label htmlFor="payment" className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">
                  {t('bookingProfile.labels.paymentMethod')}
                </label>
                <Selection
                  onChange={handlePaymentChange}
                  title={t('bookingProfile.labels.selectPaymentMethod')}
                  list={[
                    { id: 'checkout', name: t('bookingProfile.paymentMethod.checkout') },
                    { id: 'transfer', name: t('bookingProfile.paymentMethod.accountTransfer') },
                    { id: 'direct_link', name: t('bookingProfile.paymentMethod.directLink') },
                  ]}
                  value={values.paymentMethod || ''}
                  className={CREATE_BOOKING_SELECTION}
                />
                {errors.paymentMethod && touched.paymentMethod && <div className="mt-1 text-sm text-red-500">{errors.paymentMethod}</div>}
              </div>

              {values.paymentMethod === 'direct_link' && (
                <>
                  <div className="min-w-0">
                    <label htmlFor="status" className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">
                      {t('bookingProfile.labels.status')}
                    </label>
                    <Selection
                      onChange={handleStatusChange}
                      title={t('bookingProfile.labels.selectStatus')}
                      list={adminBookingStatusOptions}
                      value={values.status || ''}
                      className={CREATE_BOOKING_SELECTION}
                      error={showFieldError('status')}
                    />
                    {showFieldError('status') && <div className="mt-2 text-sm text-red-600">{errors.status}</div>}
                  </div>

                  <div className="min-w-0">
                    <label htmlFor="paymentStatus" className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">
                      {t('bookingProfile.labels.paymentStatus')}
                    </label>
                    <Selection
                      onChange={handlePaymentStatusChange}
                      title={t('bookingProfile.labels.selectPaymentStatus')}
                      list={paidUnpaidPaymentStatusOptions}
                      value={values.paymentStatus || ''}
                      className={CREATE_BOOKING_SELECTION}
                      error={showFieldError('paymentStatus')}
                    />
                    {showFieldError('paymentStatus') && <div className="mt-2 text-sm text-red-600">{errors.paymentStatus}</div>}
                  </div>
                </>
              )}

              {values.paymentMethod === 'transfer' && (
                <div className="min-w-0">
                  <label htmlFor="transferReferenceNumber" className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">
                    {t('bookingProfile.labels.transferReferenceNumber')}
                  </label>
                  <Field
                    type="text"
                    name="transferReferenceNumber"
                    as={Input}
                    id="transferReferenceNumber"
                    extraClassName="w-full"
                    className={CREATE_BOOKING_FIELD}
                    placeholder={t('bookingProfile.placeholders.transferReferenceNumber')}
                  />
                </div>
              )}
            </div>

            {values.paymentMethod === 'checkout' && (
              <div className="mt-4 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="sentPaymentEmail"
                  name="sentPaymentEmail"
                  checked={values.sentPaymentEmail || false}
                  onChange={e => setFieldValue('sentPaymentEmail', e.target.checked)}
                  className="h-4 w-4 rounded text-dark-600 focus:ring-dark-500"
                />
                <label htmlFor="sentPaymentEmail" className="text-sm text-slate-600">
                  {t('bookingProfile.labels.sentPaymentEmail')}
                </label>
              </div>
            )}

            {values.paymentMethod === 'transfer' && (
              <div className="mt-5">
                <label htmlFor="transferReceipt" className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">
                  {t('bookingProfile.labels.transferReceipt')}
                </label>
                <div className="rounded-xl border-2 border-dashed border-slate-300/90 bg-slate-50/80 px-4 py-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <label
                      htmlFor="transferReceipt"
                      className="inline-flex cursor-pointer items-center justify-center rounded-lg bg-dark-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-dark-700"
                    >
                      {t('bookingProfile.labels.chooseFile')}
                    </label>
                    <span className="text-sm text-slate-600">{transferReceiptFile?.name || t('bookingProfile.labels.noFileChosen')}</span>
                    <input
                      key={values.paymentMethod === 'transfer' ? 'transfer-receipt' : 'no-transfer'}
                      id="transferReceipt"
                      type="file"
                      accept="image/*,.pdf,application/pdf"
                      className="sr-only"
                      onChange={e => {
                        const file = e.target.files?.[0]
                        setTransferReceiptFile(file || null)
                        setFieldValue('transferReceiptFileName', file?.name || '', false)
                        setTimeout(() => {
                          validateForm()
                        }, 0)
                      }}
                    />
                  </div>
                  {errors.transferReceiptFileName && touched.transferReceiptFileName && <div className="mt-2 text-sm text-red-500">{errors.transferReceiptFileName}</div>}
                  <p className="mt-2 text-xs text-slate-500">{t('bookingProfile.hints.transferReceiptTypes')}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right: summary + actions */}
      <div className="min-w-0 lg:sticky lg:top-2 lg:self-start">
        <div className="rounded-2xl border border-sky-200/70 bg-gradient-to-b from-sky-100/50 via-sky-50/40 to-slate-50/30 p-5 shadow-md ring-1 ring-sky-100/50 sm:p-6">
          <h3 className="text-sm font-extrabold uppercase tracking-[0.16em] text-slate-800">{t('bookingProfile.labels.summary')}</h3>

          <div className="mt-4 rounded-xl border border-white/80 bg-white/70 p-1 shadow-sm">
            <div className={summaryRowClass}>
              <div className="flex min-w-0 items-center gap-2">
                <input
                  type="checkbox"
                  id="insurance"
                  name="insurance"
                  checked={values.insurance || false}
                  onChange={e => setFieldValue('insurance', e.target.checked)}
                  className="h-4 w-4 shrink-0 rounded text-dark-600 focus:ring-dark-500"
                />
                <label htmlFor="insurance" className="flex min-w-0 items-center gap-1 font-medium text-slate-800">
                  <span className="truncate">{t('bookingProfile.labels.insurance')}</span>
                  <IoInformationCircleOutline className="shrink-0 text-slate-400" />
                </label>
              </div>
              <span className="shrink-0 font-semibold tabular-nums text-slate-900">
                {formatSummaryAmount(insuranceFee)} {pricingPending ? '' : t('common.currency')}
              </span>
            </div>

            {availableExtras.map(({ key, pricePerDay }) => (
              <div key={key} className={summaryRowClass}>
                <div className="flex min-w-0 items-center gap-2">
                  <input
                    type="checkbox"
                    id={`extra-${key}`}
                    checked={_.includes(values.extras, key)}
                    onChange={e => toggleExtra(key, e.target.checked)}
                    className="h-4 w-4 shrink-0 rounded text-dark-600 focus:ring-dark-500"
                  />
                  <label htmlFor={`extra-${key}`} className="truncate font-medium text-slate-800">
                    {t(`booking.orderSummary.extra_${key}`)}
                  </label>
                </div>
                <span className="shrink-0 font-semibold tabular-nums text-slate-900">
                  {formatSummaryAmount(pricePerDay * rentalDays)} {pricingPending ? '' : t('common.currency')}
                </span>
              </div>
            ))}

            <div className={summaryRowClass}>
              <span className="flex items-center gap-1 text-slate-700">
                {t('bookingProfile.labels.depositAmount')}
                <IoInformationCircleOutline className="text-slate-400" />
              </span>
              <span className="font-semibold tabular-nums text-slate-900">
                {formatPrice(carDetail?.depositAmount || 0, true)} {t('common.currency')}
              </span>
            </div>

            <div className={summaryRowClass}>
              <span className="text-slate-700">{t('bookingProfile.labels.carRentPrice')}</span>
              <span className="font-semibold tabular-nums text-slate-900">
                {formatSummaryAmount(rentalPrice)} {pricingPending ? '' : t('common.currency')}
              </span>
            </div>

            {/* <div className={summaryRowClass}>
              <span className="text-slate-700">
                {t('bookingProfile.labels.vat')} ({vatPct}%)
              </span>
              <span className="font-semibold tabular-nums text-slate-900">
                {formatPrice(vat, true)} {t('common.currency')}
              </span>
            </div> */}

            {isDeliver && officeDeliveryCost > 0 && (
              <div className={summaryRowClass}>
                <span className="text-slate-700">{t('bookingProfile.labels.deliveryCost')}</span>
                <span className="font-semibold tabular-nums text-slate-900">
                  {formatSummaryAmount(officeDeliveryCost)} {pricingPending ? '' : t('common.currency')}
                </span>
              </div>
            )}
            <div className={summaryRowClass}>
              <span className="text-slate-700">
                {t('bookingProfile.labels.vat')} ({pricingPending ? '…' : `${vatPct}%`})
              </span>
              <span className="font-semibold tabular-nums text-slate-900">
                {formatSummaryAmount(vat + extrasVat)} {pricingPending ? '' : t('common.currency')}
              </span>
            </div>
            {isDeliver && ajarleeServiceFee > 0 && (
              <div className={summaryRowClass}>
                <span className="text-slate-700">{t('booking.orderSummary.serviceFees')}</span>
                <span className="font-semibold tabular-nums text-slate-900">
                  {formatSummaryAmount(ajarleeServiceFee)} {pricingPending ? '' : t('common.currency')}
                </span>
              </div>
            )}
          </div>

          <div className={CREATE_BOOKING_TOTAL} role="group" aria-label={t('bookingProfile.labels.totalAmountBanner')}>
            <div className="flex items-end justify-between gap-4  border-slate-300/70 pt-2">
              <span className="pb-0.5 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">{t('bookingProfile.labels.totalAmountBanner')}</span>
              <p className="text-right leading-none">
                <span className="text-2xl font-extrabold tabular-nums tracking-tight text-slate-900 sm:text-[1.75rem]">{formatSummaryAmount(totalAmount)}</span>
                <span className="ml-1.5 text-sm font-semibold text-slate-600">{pricingPending ? '' : t('common.currency')}</span>
              </p>
            </div>
          </div>

          {pricingError ? (
            <p className="mt-3 text-center text-xs text-red-600">{t('bookingProfile.messages.pricingUnavailable')}</p>
          ) : (
            pricingPending && <p className="mt-3 text-center text-xs text-slate-500">{t('bookingProfile.messages.loadingBookingDetails')}</p>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-dark-600 py-3.5 text-base font-semibold text-white shadow-md transition-colors hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-dark-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isSubmitting || pricingPending || (isCompanyBookingFlow ? !companyPaymentComplete : !values.paymentMethod)}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {t('bookingProfile.buttons.pleaseWait')}
              </span>
            ) : (
              <>
                {t('bookingProfile.buttons.createBooking')}
                <ArrowRightIcon className="h-5 w-5" aria-hidden />
              </>
            )}
          </button>

          <button type="button" onClick={onPrevious} className={classNames(CREATE_BOOKING_BTN_PREV, 'mt-3 w-full')}>
            {t('bookingProfile.buttons.previous')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ReviewStep
